import { inject, Service } from '@angular/core';
import { MngHttpService } from '@mng/mng-http';
import { HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import {
  IAuditlog,
  IAuditlogSearch,
  IBatchInProgressInfo,
  ICmsContent,
  ICmsContentList,
  ICodePruefungByName,
  ICodetableItem,
  IDienstleistungAbschlussInfo,
  IDienstleistungArtInfo,
  IDienstleistungBasisDatenUpdate,
  IDienstleistungBearbeitung,
  IDienstleistungBuchen,
  IDienstleistungErfassung,
  IDienstleistungInfo,
  IDienstleistungKundeUpdate,
  IDienstleistungLeistungArtZuordnung,
  IDienstleistungResult,
  IDienstleistungSucheParams,
  IDienstleistungUpdate,
  IExternePartnerZuordnungDto,
  IFallPartnerDaten,
  IFallPartnerDetail,
  IGesundheitsPartnerZuordnung,
  IGesundheitsProgramm,
  IGesundheitsProgrammInfo,
  IHasIdAndVersion,
  IKidsBuchungAppAvailableResult,
  IKidsBuchungDatenResult,
  IKidsDruckAnforderung,
  IKidsHotel,
  IKidsImportInfo,
  IKidsPotentielle,
  IKidsPotentielleDaten,
  IKidsPotentielleSuche,
  IKidsReservierung,
  IKidsTermin,
  IKidsTermineAnzahlDaten,
  IKunde,
  IKundeSuche,
  ILaborProtokoll,
  ILaborProtokollDaten,
  ILaborProtokollParams,
  ILaborProtokollStatusWechsel,
  ILaborProtokollZuordnungAenderung,
  ILeistungAbrechnung,
  ILeistungAbrechnungen,
  ILeistungAbrechnungenParams,
  ILeistungAbrechnungParams,
  ILeistungDefinitionContainer,
  ILeistungDefinitionImportParams,
  ILeistungDefinitionParams,
  ILeistungen,
  ILeisungDefinitionImportResult,
  IMessages,
  IMgsClientProps,
  IPartner,
  IPartnerInfo,
  IResult,
  ITextbausteinContainer,
  ITextbausteinUpdate,
  IUser,
  IUserPasswortAenderung
} from '@mng/mgs-lib';
import { EnvironmentInformation } from '@mng/mng-config';
import { ILogin } from './login/login.form';

export const MGS_LOGIN_URL = 'api/public/user/login';

@Service()
export class MgsWebApi {
  private httpService = inject(MngHttpService);

  constructor() {
    const httpService = this.httpService;

    httpService.setPreventErrorHandling(true);
  }

  dienstleistung(code: string): Observable<IDienstleistungResult> {
    return this.httpService.post<IDienstleistungResult>(`api/protected/dienstleistung/${code}`, {});
  }

  demoDienstleistung(programmId: number): Observable<IDienstleistungResult> {
    return this.httpService.post<IDienstleistungResult>(`api/protected/demodienstleistung/${programmId}`, {});
  }

  demoDienstleistungenLoeschen(): Observable<void> {
    return this.httpService.delete<void>(`api/protected/demodienstleistung`);
  }

  dienstleistungFuerAdmin(code: string): Observable<IResult<IDienstleistungInfo>> {
    return this.httpService.get<IResult<IDienstleistungInfo>>(`api/admin/dienstleistung/${code}`);
  }

  dienstleistungFuerAdminSpeichern(dienstleistung: IDienstleistungInfo): Observable<IResult<IDienstleistungInfo>> {
    return this.httpService.post<IResult<IDienstleistungInfo>>(`api/admin/dienstleistung`, dienstleistung);
  }

  dienstleistungFuerAdminLoeschen(id: number): Observable<void> {
    return this.httpService.delete<void>(`api/admin/dienstleistung/${id}`);
  }

  dienstleistungArten(): Observable<IDienstleistungArtInfo[]> {
    return this.httpService.get<IDienstleistungArtInfo[]>('api/protected/dienstleistungsarten');
  }

  dienstleistungenSuche(params: IDienstleistungSucheParams): Observable<IResult<IDienstleistungInfo[]>> {
    return this.httpService.post<IResult<IDienstleistungInfo[]>>(`api/protected/dienstleistung/suche`, params);
  }

  setzeDienstleistungInBearbeitung(bearbeitung: IDienstleistungBearbeitung): Observable<IDienstleistungResult> {
    return this.httpService.post<IDienstleistungResult>(`api/protected/setzedienstleistunginbearbeitung`, bearbeitung);
  }

  dienstleistungenSchnellabschlussPruefung(codes: string[]): Observable<IResult<IDienstleistungInfo>[]> {
    return this.httpService.post<IResult<IDienstleistungInfo>[]>(
      `api/protected/dienstleistung/schnellabschluss/pruefen`,
      codes
    );
  }

  dienstleistungenSchnellabschluss(
    dienstleistungen: IDienstleistungInfo[]
  ): Observable<IResult<IDienstleistungInfo>[]> {
    return this.httpService.post<IResult<IDienstleistungInfo>[]>(
      `api/protected/dienstleistung/schnellabschluss`,
      dienstleistungen
    );
  }

  dienstleistungCodePruefung(codes: string[]): Observable<IResult<IDienstleistungInfo>[]> {
    return this.httpService.post<IResult<IDienstleistungInfo>[]>(
      `api/protected/dienstleistung/buchen/codepruefung`,
      codes
    );
  }

  dienstleistungCodePruefungNachName(codePruefung: ICodePruefungByName): Observable<IResult<IDienstleistungInfo[]>> {
    return this.httpService.post<IResult<IDienstleistungInfo[]>>(
      `api/protected/dienstleistung/buchen/codepruefungnachname`,
      codePruefung
    );
  }

  dienstleistungBuchen(buchenInfo: IDienstleistungBuchen): Observable<IResult<IDienstleistungInfo>> {
    return this.httpService.post<IResult<IDienstleistungInfo>>(`api/protected/dienstleistung/buchen`, buchenInfo);
  }

  dienstleistungBuchenWechselprogramme(
    buchenInfo: IDienstleistungBuchen
  ): Observable<IResult<IGesundheitsProgrammInfo[]>> {
    return this.httpService.post<IResult<IGesundheitsProgrammInfo[]>>(
      `api/protected/dienstleistung/buchen/wechselprogramme`,
      buchenInfo
    );
  }

  dienstleistungRueckbuchen(dienstleistung: IDienstleistungInfo): Observable<IResult<IDienstleistungInfo>> {
    return this.httpService.post<IResult<IDienstleistungInfo>>(
      `api/protected/dienstleistung/rueckbuchen`,
      dienstleistung
    );
  }

  dienstleistungSpeichern(update: IDienstleistungUpdate): Observable<IDienstleistungUpdate> {
    return this.httpService.post<IDienstleistungUpdate>(`api/protected/dienstleistung`, update);
  }

  dienstleistungErfassungSpeichern(erfassung: IDienstleistungErfassung): Observable<IResult<IDienstleistungInfo>> {
    return this.httpService.post<IResult<IDienstleistungInfo>>(`api/admin/dienstleistung/erfassung`, erfassung);
  }

  dienstleistungenAsCsv(dienstleistungen: IDienstleistungInfo[]): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlobWithPost('api/protected/dienstleistung/csv', dienstleistungen);
  }

  partnerList(): Observable<IResult<IPartnerInfo[]>> {
    return this.httpService.get<IResult<IPartnerInfo[]>>('api/protected/partner/admin/list');
  }

  buchungPartnerIdsErmittlung(): Observable<number[]> {
    return this.httpService.get<number[]>('api/protected/partner/buchungpartner/ids');
  }

  programme(): Observable<IResult<IGesundheitsProgrammInfo[]>> {
    return this.httpService.get<IResult<IGesundheitsProgrammInfo[]>>('api/public/programm/list');
  }

  programm(id: number): Observable<IResult<IGesundheitsProgramm>> {
    return this.httpService.get<IResult<IGesundheitsProgramm>>(`api/protected/programm/admin/${id}`);
  }

  programmSpeichern(programm: IGesundheitsProgramm): Observable<IResult<IGesundheitsProgramm>> {
    return this.httpService.post<IResult<IGesundheitsProgramm>>(`api/protected/programm/admin/save`, programm);
  }

  userList(partnerId?: number): Observable<IUser[]> {
    const partnerParam = partnerId ? `/${partnerId}` : '?auchInaktive=true';
    return this.httpService.get<IUser[]>(`api/protected/user/admin/list${partnerParam}`);
  }

  userSpeichern(user: IUser): Observable<IUser> {
    return this.httpService.post<IUser>('api/protected/user/admin', user);
  }

  userPasswortReset(userId: string): Observable<void> {
    return this.httpService.post<void>(`api/public/user/passwortReset/${userId}`, {});
  }

  userPasswortResetAdmin(id: number): Observable<void> {
    return this.httpService.post<void>(`api/protected/user/admin/passwortReset/${id}`, {});
  }

  deleteUser(id: number): Observable<void> {
    return this.httpService.delete<void>(`api/protected/user/admin/${id}`);
  }

  userPasswortAenderung(aenderung: IUserPasswortAenderung) {
    return this.httpService.post<void>(`api/public/user/passwortAenderung`, aenderung);
  }

  partner(id: number): Observable<IResult<IPartner>> {
    return this.httpService.get<IResult<IPartner>>(`api/protected/partner/admin/${id}`);
  }

  bundeslaender(): Observable<IResult<ICodetableItem[]>> {
    return this.httpService.get<IResult<ICodetableItem[]>>(`api/public/bundeslaender/list`);
  }

  codetableEntries(types?: 'Kids' | undefined): Observable<{ [type: string]: ICodetableItem[] }> {
    const typesParams = types ? `?codetableTypes=${types}` : '';
    return this.httpService.get<{ [type: string]: ICodetableItem[] }>(`api/public/codetable${typesParams}`);
  }

  // bei Kunden Aenderung wird am Server nur ein mail versendet
  kundenAenderung(update: IDienstleistungKundeUpdate): Observable<IDienstleistungKundeUpdate> {
    return this.httpService.post<IDienstleistungKundeUpdate>(`api/protected/dienstleistung/kunde`, update);
  }

  // bei Kunden Aenderung wird am Server nur ein mail versendet
  dienstleistungBasisDatenAenderung(update: IDienstleistungBasisDatenUpdate): Observable<IHasIdAndVersion> {
    return this.httpService.post<IHasIdAndVersion>(`api/protected/dienstleistung/basisdaten`, update);
  }

  leistungDefinitionContainer(params?: ILeistungDefinitionParams): Observable<ILeistungDefinitionContainer> {
    return this.httpService.post<ILeistungDefinitionContainer>(`api/protected/leistungsdef`, params);
  }

  fallPartnerDetails(partnerId?: number): Observable<IFallPartnerDaten> {
    const params = partnerId ? `?partnerId=${partnerId}` : '';
    return this.httpService.get<IFallPartnerDaten>(`api/protected/fall/partnerdetails${params}`);
  }

  adminFalldatenZaehlungUpdate(): Observable<void> {
    return this.httpService.post<void>(`api/protected/fall/admin/udpatefallzaehlung`);
  }

  adminLeistungRechnungExport(): Observable<void> {
    return this.httpService.post<void>(`api/protected/leistung/admin/rechnungexport`);
  }

  adminLabordatenImport(): Observable<void> {
    return this.httpService.post<void>(`api/protected/laborprotokoll/admin/import`);
  }

  updateFallPartnerDetail(detail: IFallPartnerDetail): Observable<IFallPartnerDetail> {
    return this.httpService.post<IFallPartnerDetail>(`api/protected/fall/admin/partnerdetail`, detail);
  }

  partnerSpeichern(partner: IPartner): Observable<IResult<IPartner>> {
    return this.httpService.post<IResult<IPartner>>(`api/protected/partner/admin/save`, partner);
  }

  kundenSuche(params: IKundeSuche): Observable<IResult<IKunde[]>> {
    return this.httpService.post<IResult<IKunde[]>>(`api/protected/kunden`, params);
  }

  kunde(id: number): Observable<IResult<IKunde>> {
    return this.httpService.get<IResult<IKunde>>(`api/protected/kunde/${id}`);
  }

  textbausteinContainer(dienstleistungId: number): Observable<IResult<ITextbausteinContainer>> {
    return this.httpService.get<IResult<ITextbausteinContainer>>(
      `api/protected/textbaustein/daten/${dienstleistungId}`
    );
  }

  textbausteinUpdate(update: ITextbausteinUpdate): Observable<IResult<ITextbausteinUpdate>> {
    return this.httpService.post<IResult<ITextbausteinUpdate>>(`api/protected/textbaustein/daten`, update);
  }

  importLeistungDefinitionen(
    files: File[],
    importInfo: ILeistungDefinitionImportParams
  ): Observable<{ [fileName: string]: ILeisungDefinitionImportResult }> {
    const formData: FormData = new FormData();

    for (const file of files) {
      formData.append('file', file);
    }

    formData.append('importInfo', new Blob([JSON.stringify(importInfo)], { type: 'application/json' }));

    return this.httpService.post('api/protected/leistung/importLeistungDefinitionen', formData);
  }

  leistungen(params: ILeistungAbrechnungParams): Observable<IResult<ILeistungen>> {
    return this.httpService.post<IResult<ILeistungen>>('api/protected/leistung', params);
  }

  leistungenFuerAbrechnung(abechnungId: number): Observable<IResult<ILeistungen>> {
    return this.httpService.get<IResult<ILeistungen>>(`api/protected/leistungenfuerabrechnung/${abechnungId}`);
  }

  leistungenAnfordern(leistungen: ILeistungen) {
    return this.httpService.post<IResult<ILeistungAbrechnung>>('api/protected/leistung/anfordern', leistungen);
  }

  async leistungAbrechnungVorschauPdf(
    leistungen: ILeistungen,
    detailed = true
  ): Promise<{ blob: Blob; filename: string }> {
    const detailedParam = detailed ? 'true' : 'false';
    return this.httpService.getBlobWithPost(
      `api/protected/leistung/pseudoabrechnung?detailed=${detailedParam}`,
      leistungen
    );
  }

  abrechnungen(params: ILeistungAbrechnungenParams = {}): Observable<IResult<ILeistungAbrechnungen>> {
    return this.httpService.post<IResult<ILeistungAbrechnungen>>('api/protected/leistung/abrechnungen', params);
  }

  laborProtokolle(params: ILaborProtokollParams = {}): Observable<IResult<ILaborProtokollDaten>> {
    return this.httpService.post<IResult<ILaborProtokollDaten>>('api/protected/laborprotokoll', params);
  }

  laborProtokollBearbeitung(daten: ILaborProtokollStatusWechsel): Observable<void> {
    return this.httpService.post<void>('api/protected/laborprotokoll/bearbeitung', daten);
  }

  laborProtokollZuordnungAenderung(daten: ILaborProtokollZuordnungAenderung): Observable<IResult<ILaborProtokoll>> {
    return this.httpService.post<IResult<ILaborProtokoll>>('api/protected/laborprotokoll/zuordnungaenderung', daten);
  }

  laborProtokoll(id: number): Observable<IResult<ILaborProtokoll>> {
    return this.httpService.get<IResult<ILaborProtokoll>>(`api/protected/laborprotokoll/${id}`);
  }

  async laborProtokollPdf(id: number): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/protected/laborprotokoll/pdf/${id}`);
  }

  async abrechnungPdf(id: number, detailed = false): Promise<{ blob: Blob; filename: string }> {
    const detailedParam = `?detailed=${detailed ? 'true' : 'false'}`;
    return this.httpService.getBlob(`api/protected/leistung/abrechnung/pdf/${id}${detailedParam}`);
  }

  async dienstleistungAbschlussBerichtPdf(id: number): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/protected/dienstleistung/abschlussbericht/${id}`);
  }

  async untersuchungsVergleichPdf(
    dienstleistungId: number,
    attributDefId: number
  ): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(
      `api/protected/dienstleistung/attributvergleich/${dienstleistungId}/${attributDefId}`
    );
  }

  dienstleistungAbschlussInfo(id: number): Observable<IResult<IDienstleistungAbschlussInfo>> {
    return this.httpService.get<IResult<IDienstleistungAbschlussInfo>>(
      `api/protected/dienstleistung/abschlussinfo/${id}`
    );
  }

  dienstleistungAbschluss(id: number): Observable<void> {
    return this.httpService.post(`api/protected/dienstleistung/abschluss/${id}`, {});
  }

  async abrechnungCsv(id: number): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/protected/leistung/abrechnung/csv/${id}`);
  }

  adminDeleteDienstleistungenWithNichtDatenspeichern(): Observable<IResult<number>> {
    return this.httpService.delete<IResult<number>>('api/admin/dienstleistung/loeschenmitnichtdatenspeichern');
  }

  envInfo(): Observable<EnvironmentInformation<IMgsClientProps>> {
    return this.httpService.get<EnvironmentInformation<IMgsClientProps>>('envinfo/api/public/envInfo');
  }

  dienstleistungsBearbeitungStatusWechsel(update: {
    id: number;
    statusId: number;
  }): Observable<IResult<IDienstleistungInfo>> {
    return this.httpService.post<IResult<IDienstleistungInfo>>('api/protected/dientleistung/statuschange', update);
  }

  updateExterneLeistungArtZuordnungen(
    externeZuordnungen: IDienstleistungLeistungArtZuordnung[]
  ): Observable<IResult<IDienstleistungLeistungArtZuordnung[]>> {
    return this.httpService.post<IResult<IDienstleistungLeistungArtZuordnung[]>>(
      'api/protected/dienstleistung/externeleistung',
      externeZuordnungen
    );
  }

  externePartnerZuordnungenFuerDienstleistung(
    dienstleistungId: number
  ): Observable<IResult<IGesundheitsPartnerZuordnung[]>> {
    return this.httpService.get<IResult<IGesundheitsPartnerZuordnung[]>>(
      `api/protected/partner/externezuordnungen/${dienstleistungId}`
    );
  }

  externePartnerZuordnungenFuerPartner(partnerId: number): Observable<IResult<IExternePartnerZuordnungDto[]>> {
    return this.httpService.get<IResult<IGesundheitsPartnerZuordnung[]>>(
      `api/protected/partner/externezuordnungen/partner/${partnerId}`
    );
  }

  externePartnerZuordnungLoeschen(zuordnungId: number): Observable<void> {
    return this.httpService.delete(`api/protected/partner/externezuordnung/${zuordnungId}`);
  }

  externePartnerZuordnungAnlegen(zuordnung: {
    partnerId: number;
    externePartnerId: number;
  }): Observable<IResult<IExternePartnerZuordnungDto>> {
    return this.httpService.post<IResult<IExternePartnerZuordnungDto>>(
      `api/protected/partner/externezuordnung`,
      zuordnung
    );
  }

  async login(login: ILogin) {
    await firstValueFrom(this.httpService.post(MGS_LOGIN_URL, login));
  }
  async logout() {
    await firstValueFrom(this.httpService.post('api/public/user/logout', {}));
  }

  kidsPotentielle(suche: IKidsPotentielleSuche = {}): Observable<IResult<IKidsPotentielleDaten>> {
    return this.httpService.post<IResult<IKidsPotentielleDaten>>('api/kids/kidspotentielle', suche);
  }
  kidSpeichern(kid: IKidsPotentielle): Observable<IResult<IKidsPotentielle>> {
    return this.httpService.post<IResult<IKidsPotentielle>>('api/kids/kidspotentielle/save', kid);
  }

  kidsFuerTermin(kidsTerminId: number): Observable<IResult<IKidsPotentielleDaten>> {
    return this.kidsPotentielle({ kidsTerminId });
  }

  kidsTermine(): Observable<IKidsTermin[]> {
    return this.httpService.get<IKidsTermin[]>('api/kidstermin/termine');
  }

  kidsTerminSpeichern(termin: IKidsTermin): Observable<IKidsTermin> {
    return this.httpService.post<IKidsTermin>('api/kidstermin/save', termin);
  }

  kidsHotels(): Observable<IKidsHotel[]> {
    return this.httpService.get<IKidsTermin[]>('api/kidshotel/list');
  }

  kidsHotelSave(hotel: IKidsHotel): Observable<IKidsHotel> {
    return this.httpService.post<IKidsTermin>('api/kidshotel/save', hotel);
  }

  kidsTermineAnzahlDatenErmittlung(): Observable<IKidsTermineAnzahlDaten> {
    return this.httpService.get<IKidsTermineAnzahlDaten>('api/kidstermin/anzahlermittlung');
  }

  kidsBuchungAppAvailable(): Observable<IKidsBuchungAppAvailableResult> {
    return this.httpService.get<IKidsBuchungAppAvailableResult>('api/kidsbuchung/appAvailable');
  }

  kidsBuchungLogin(code: string): Observable<void> {
    return this.httpService.post<void>('api/kidsbuchung/login', { code });
  }

  kidsBuchungDaten(): Observable<IKidsBuchungDatenResult> {
    return this.httpService.get<IKidsBuchungDatenResult>('api/kidsbuchung/daten');
  }

  kidsTerminReservierung(reservierung: IKidsReservierung): Observable<IMessages> {
    return this.httpService.post<IMessages>('api/kidsbuchung/reservierung', reservierung);
  }
  kidsTerminWarteliste(reservierung: IKidsReservierung): Observable<IMessages> {
    return this.httpService.post<IMessages>('api/kidsbuchung/warteliste', reservierung);
  }

  async kidsWartelisteCsv(): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/kidstermin/csv/warteliste`);
  }

  async kidsOnlineReservierungDatenCsv(): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/kids/onlinereservierungsdaten`);
  }

  async kidsTeilgenommenFuerAbrechnungCsv(): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/protected/kids/csv/abrechnung/teilgenommene`);
  }

  async kidsUnentschuldigtFuerAbrechnungCsv(): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/protected/kids/csv/abrechnung/unentschuldigte`);
  }

  kidsTerminReservierungAufheben(reservierung: IKidsReservierung): Observable<IMessages> {
    return this.httpService.post<IMessages>('api/kidsbuchung/reservierungAufheben', reservierung);
  }
  kidsTerminWartelisteAufheben(reservierung: IKidsReservierung): Observable<IMessages> {
    return this.httpService.post<IMessages>('api/kidsbuchung/wartelisteAufheben', reservierung);
  }

  terminWartenlisteFuerKid(kidId: number): Observable<IKidsTermin[]> {
    return this.httpService.get<IKidsTermin[]>(`api/kids/warteliste/${kidId}`);
  }

  terminWartenlisteFuerKidLoeschen(kidId: number): Observable<void> {
    return this.httpService.delete<void>(`api/kids/warteliste/${kidId}`);
  }

  kidsFuerTerminWarteliste(terminId: number): Observable<IKidsPotentielle[]> {
    return this.httpService.get<IKidsPotentielle[]>(`api/kids/warteliste/${terminId}`);
  }

  kidsTerminPdf(terminId: number): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/kidstermin/pdf/${terminId}`);
  }

  kidsTerminCsv(terminId: number): Promise<{ blob: Blob; filename: string }> {
    return this.httpService.getBlob(`api/kidstermin/csv/${terminId}`);
  }

  kidsDatenImportStarten(file: File, importInfo: IKidsImportInfo, asyncImport = true): Observable<void> {
    const url = `api/protected/kids/import${!asyncImport ? 'sync' : ''}`;

    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('importInfo', new Blob([JSON.stringify(importInfo)], { type: 'application/json' }));
    return this.httpService.post(url, formData);
  }

  kidsImportInProgressInfo(): Observable<IBatchInProgressInfo> {
    return this.httpService.get<IBatchInProgressInfo>('api/protected/kids/importinfo');
  }

  kidsDruckAnforderung(anforderung: IKidsDruckAnforderung) {
    return this.httpService.post('/api/protected/kids/druckanforderung', anforderung);
  }

  cmsHistory(context: string, id: number): Observable<IResult<ICmsContentList>> {
    const params = `context=${context}&id=${id}`;
    return this.httpService.get<IResult<ICmsContentList>>(`/api/protected/cms/admin/history?${params}`);
  }

  cmsDetail(id: number): Observable<IResult<ICmsContentList>> {
    return this.httpService.get<IResult<ICmsContentList>>(`/api/protected/cms/admin/${id}`);
  }

  cmsDetailEntfernen(id: number): Observable<IResult<ICmsContentList>> {
    return this.httpService.delete<IResult<ICmsContentList>>(`/api/protected/cms/admin/${id}`);
  }

  cmsDetailSave(content: ICmsContent): Observable<IResult<ICmsContent>> {
    return this.httpService.post<IResult<ICmsContent>>(`/api/protected/cms/admin/save`, content);
  }

  cmsDetailForPreview(entityId: string, context: string, cmsContent: object): Observable<IResult<string>> {
    return this.httpService.post<IResult<string>>(`/api/protected/cms/admin/preview`, {
      entityId,
      context,
      cmsContent
    });
  }

  async cmsDetailForRendering(context: string, id: number) {
    const params = `context=${context}&id=${id}`;
    const result = await this.httpService.getTextAsPromise(`/api/public/cms/content?${params}`);
    return result;
  }

  auditlog(search: IAuditlogSearch): Observable<IAuditlog[]> {
    return this.httpService.post<IAuditlog[]>('/api/protected/admin/auditlog', search);
  }

  auditlogContexte(): Observable<string[]> {
    return this.httpService.get<string[]>('/api/protected/admin/auditlog/contexte');
  }

  adminResetCache(): Observable<void> {
    return this.httpService.post<void>('/api/protected/admin/resetcache', {});
  }
}

export function createRequestHeaders(accept: string): HttpHeaders {
  let headers: HttpHeaders = new HttpHeaders();
  headers = headers.append('Accept', accept);
  return headers;
}

export function createDefaultRequestHeaders(): HttpHeaders {
  return createRequestHeaders('application/json');
}

export function createPdfRequestHeaders(): HttpHeaders {
  return createRequestHeaders('application/pdf');
}

export function createHtmlRequestHeaders(): HttpHeaders {
  return createRequestHeaders('text/html');
}
